import React, {
  useState, useMemo, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import qs from 'qs';
import {
  selectServicePlanWorkflowState,
  selectCurrentWorkflowDefinition,
  selectServicePlanWorkflowErrorList,
  updateSingleField,
} from '../../../../redux/slices/servicePlanWorkflowSlice/servicePlanWorkflowSlice';
import {
  selectServicePlan,
  selectContactInfo, selectHasValidPromoEngagementDiscount,
} from '../../../../redux/slices/billingSlice/billingSlice';
import envConfig from '../../../../envConfig';
import SERVICE_PLAN_WORKFLOW_TYPE, {
  SERVICE_PLAN_PAYMENT_PERIOD,
} from '../../../../utility/types/ServicePlanWorkflowTypes';
import ServicePlanOrderSummary, { CREDIT_LABEL } from './ServicePlanOrderSummary';
import PODocumentsForm from './PODocumentsForm';
import PaymentMethodForm from './PaymentMethodForm';
import ServiceAddressModal from '../modals/ServiceAddressModal';

export { CREDIT_LABEL };

function ServicePlanSummaryForm({ onChangePlan, countryList, onTaxLoadingChange }) {
  const dispatch = useDispatch();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isTaxLoading, setIsTaxLoading] = useState(false);

  useEffect(() => {
    onTaxLoadingChange(isTaxLoading);
  }, [isTaxLoading, onTaxLoadingChange]);

  const {
    selectedServicePlan, serviceSelection, orderSummary, paymentMethod,
  } = useSelector(
    selectServicePlanWorkflowState,
  );
  const workflowDef = useSelector(selectCurrentWorkflowDefinition);
  const errorList = useSelector(selectServicePlanWorkflowErrorList);
  const activeServicePlan = useSelector(selectServicePlan);
  const contactInfo = useSelector(selectContactInfo);
  const hasValidPromoEngagementDiscount = useSelector(selectHasValidPromoEngagementDiscount);

  const isUpgrade = useMemo(() => (
    workflowDef.id === SERVICE_PLAN_WORKFLOW_TYPE.CHANGEPLAN.id
  ), [workflowDef.id]);

  const isPayBalance = useMemo(() => (
    workflowDef.id === SERVICE_PLAN_WORKFLOW_TYPE.PAYBALANCE.id
  ), [workflowDef.id]);

  const isPayInAdvance = useMemo(() => (
    workflowDef.id === SERVICE_PLAN_WORKFLOW_TYPE.PAYINADVANCE.id
  ), [workflowDef.id]);

  const pricing = useMemo(() => {
    if (!selectedServicePlan) return {};
    const monthly = parseFloat(selectedServicePlan.monthlyPrice);
    const yearly = parseFloat(selectedServicePlan.yearlyPrice);
    const { name } = selectedServicePlan;
    const isYearly = serviceSelection.selectedBillingCycle === SERVICE_PLAN_PAYMENT_PERIOD.YEARLY;
    const additionalYears = serviceSelection.additionalYears || 0;
    const totalYears = 1 + additionalYears;
    const isPayBalanceWorkflow = workflowDef.id === SERVICE_PLAN_WORKFLOW_TYPE.PAYBALANCE.id;

    let hasYearlyDiscount = false;
    let discountPct = 0;
    let yearlyDiscountedPrice = yearly;

    if (hasValidPromoEngagementDiscount) {
      const promo = activeServicePlan.promotionalEngagement;
      const rawDiscount = Number.isFinite(parseFloat(promo.discount))
        ? parseFloat(promo.discount)
        : 0;
      hasYearlyDiscount = isYearly && rawDiscount > 0 && rawDiscount < 1;
      if (hasYearlyDiscount) {
        discountPct = Math.round(rawDiscount * 100);
        yearlyDiscountedPrice = Math.floor(yearly * (1 - rawDiscount) * 100) / 100;
      }
    }

    const yearLabel = (n) => `${n} ${n === 1 ? 'year' : 'years'}`;

    let primaryLabel;
    let primarySubLabel = null;
    let primaryPrice;
    let extraLabel = null;
    let extraPrice = null;

    if (isPayBalanceWorkflow) {
      primaryLabel = `${name} - balance`;
      primaryPrice = activeServicePlan ? (parseFloat(activeServicePlan.balance) || 0) : 0;
    } else if (isPayInAdvance) {
      primaryLabel = `${name} - ${yearLabel(additionalYears)}`;
      primaryPrice = yearly * additionalYears;
    } else if (!isYearly) {
      primaryLabel = `${name} (Monthly)`;
      primaryPrice = monthly;
    } else if (hasYearlyDiscount && additionalYears > 0) {
      primaryLabel = `${name} - 1 year`;
      primarySubLabel = ` (${discountPct}% discount)`;
      primaryPrice = yearlyDiscountedPrice;
      extraLabel = `${name} - ${yearLabel(additionalYears)}`;
      extraPrice = yearly * additionalYears;
    } else if (hasYearlyDiscount) {
      primaryLabel = `${name} - 1 year`;
      primarySubLabel = ` (${discountPct}% discount)`;
      primaryPrice = yearlyDiscountedPrice;
    } else if (additionalYears > 0) {
      primaryLabel = `${name} - ${yearLabel(totalYears)}`;
      primaryPrice = yearly * totalYears;
    } else {
      primaryLabel = `${name} - 1 year`;
      primaryPrice = yearly;
    }

    return {
      primaryLabel,
      primarySubLabel,
      primaryPrice,
      extraLabel,
      extraPrice,
      subtotal: primaryPrice + (extraPrice || 0),
    };
  }, [selectedServicePlan, serviceSelection.selectedBillingCycle,
    serviceSelection.additionalYears, workflowDef.id, activeServicePlan,
    isPayInAdvance, hasValidPromoEngagementDiscount]);

  const credit = useMemo(() => {
    const isChangePlanWorkflow = [
      SERVICE_PLAN_WORKFLOW_TYPE.CHANGEPLAN.id,
      SERVICE_PLAN_WORKFLOW_TYPE.CHANGEPAYMENTPERIOD.id,
    ].includes(workflowDef.id);

    let creditLabel = CREDIT_LABEL.ACCOUNT_CREDIT;

    let creditAmount = null;
    if (activeServicePlan) {
      const planCredit = activeServicePlan.credit || 0;
      if (isChangePlanWorkflow) {
        const total = planCredit + (activeServicePlan.valueOfRemainingServiceCredit || 0);
        if (total > 0) {
          creditLabel = CREDIT_LABEL.EXISTING_SERVICE_CREDIT;
        }
        creditAmount = total > 0 ? total : null;
      } else {
        creditAmount = planCredit > 0 ? planCredit : null;
      }
    }

    return { label: creditLabel, amount: creditAmount };
  }, [workflowDef.id, activeServicePlan]);

  const fetchTax = useCallback(() => {
    if (!selectedServicePlan) return;
    setIsTaxLoading(true);
    axios.post(
      `${envConfig.raaUrl}/subscription/billing/data/service-plan-tax`,
      qs.stringify({
        tierId: selectedServicePlan.id,
        termInterval: serviceSelection.selectedBillingCycle === SERVICE_PLAN_PAYMENT_PERIOD.YEARLY ? 'year' : 'month',
        additionalYears: serviceSelection.additionalYears || 0,
        isUpgrade: String(isUpgrade),
        isPayBalance: String(isPayBalance),
        isPayInAdvance: String(isPayInAdvance),
      }),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        withCredentials: true,
      },
    ).then((response) => {
      const payload = response && response.data ? response.data : null;
      const result = payload && payload.data ? payload.data : payload;
      if (result && result.tax !== undefined) {
        dispatch(updateSingleField({ field: 'orderSummary.taxAmount', value: result.tax }));
      }
    }).catch(() => {}).finally(() => {
      setIsTaxLoading(false);
    });
  }, [
    dispatch,
    selectedServicePlan,
    serviceSelection.selectedBillingCycle,
    serviceSelection.additionalYears,
    isUpgrade,
    isPayBalance,
    isPayInAdvance,
  ]);

  const downloadServicePlanPdf = useCallback(async (documentType) => {
    if (!selectedServicePlan) return;

    try {
      const response = await axios({
        url: `${envConfig.raaUrl}/subscription/billing/service-plan-pdf`,
        method: 'POST',
        data: qs.stringify({
          type: documentType,
          tierId: selectedServicePlan.id,
          recurringInterval: serviceSelection.selectedBillingCycle,
          additionalYears: serviceSelection.additionalYears || 0,
          isUpgrade: String(isUpgrade),
          isPayBalance: String(isPayBalance),
          purchaseOrderNumber: orderSummary.purchaseOrderNumber || '',
        }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        responseType: 'blob',
        withCredentials: true,
      });

      const contentType = response.headers['content-type'] || '';

      if (!contentType.includes('application/pdf')) {
        const responseText = response.data instanceof Blob
          ? await response.data.text()
          : String(response.data || '');

        // eslint-disable-next-line no-console
        console.error('Expected service plan PDF response, but received non-PDF response:', {
          status: response.status,
          contentType,
          body: responseText,
        });

        return;
      }

      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });

      if (!blob.size) {
        // eslint-disable-next-line no-console
        console.error('Service plan PDF response was empty.');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const contentDisposition = response.headers['content-disposition'];

      let filename = documentType === 'invoice' ? 'RAS-Invoice.pdf' : 'RAS-Quote.pdf';

      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

        if (filenameStarMatch) {
          filename = decodeURIComponent(filenameStarMatch[1]);
        } else if (filenameMatch) {
          // eslint-disable-next-line prefer-destructuring
          filename = filenameMatch[1];
        }
      }

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      if (error.response) {
        const contentType = error.response.headers
          ? error.response.headers['content-type']
          : '';

        let responseBody = '';

        if (error.response.data instanceof Blob) {
          responseBody = await error.response.data.text();
        } else if (typeof error.response.data === 'string') {
          responseBody = error.response.data;
        } else if (error.response.data) {
          responseBody = JSON.stringify(error.response.data);
        }

        // eslint-disable-next-line no-console
        console.error('Failed to download service plan PDF:', {
          status: error.response.status,
          statusText: error.response.statusText,
          contentType,
          body: responseBody,
        });

        return;
      }

      // eslint-disable-next-line no-console
      console.error('Failed to download service plan PDF:', error);
    }
  }, [
    selectedServicePlan,
    serviceSelection.selectedBillingCycle,
    serviceSelection.additionalYears,
    isUpgrade,
    isPayBalance,
    orderSummary.purchaseOrderNumber,
  ]);

  useEffect(() => {
    fetchTax();
  }, [fetchTax, contactInfo.serviceAddress]);

  const tax = orderSummary.taxAmount || 0;
  const creditDeduction = credit.amount || 0;
  const subtotal = Math.max(0, (pricing.subtotal || 0) - creditDeduction);
  const total = Math.max(0, subtotal + tax);

  useEffect(() => {
    dispatch(updateSingleField({
      field: 'orderSummary',
      value: {
        ...orderSummary,
        primaryPrice: pricing.primaryPrice || 0,
        extraPrice: pricing.extraPrice || 0,
        pricingSubtotal: pricing.subtotal || 0,
        creditAmount: creditDeduction,
        subtotal,
        taxAmount: tax,
        totalAmount: total,
      },
    }));
  }, [
    dispatch,
    pricing.primaryPrice,
    pricing.extraPrice,
    pricing.subtotal,
    creditDeduction,
    subtotal,
    tax,
    total,
  ]);

  const hasCardOnFile = !!(contactInfo && contactInfo.cardOnFile
    && contactInfo.cardOnFile.cardNumber);
  const cardOnFileLastFour = (contactInfo && contactInfo.cardOnFile && contactInfo.cardOnFile.cardNumber) || '';

  if (!selectedServicePlan) return null;

  return (
    <div>
      <div className="mb-4">
        <ServicePlanOrderSummary
          primaryServiceLabel={pricing.primaryLabel}
          primaryServiceSubLabel={pricing.primarySubLabel}
          primaryServicePrice={pricing.primaryPrice}
          onChangePlan={onChangePlan}
          extraServiceLabel={pricing.extraLabel}
          extraServicePrice={pricing.extraPrice}
          creditLabel={credit.amount ? credit.label : null}
          creditAmount={credit.amount}
          subtotal={subtotal}
          tax={tax}
          onChangeTax={() => setShowAddressModal(true)}
          total={total}
          isTaxLoading={isTaxLoading}
        />
      </div>
      <PODocumentsForm
        purchaseOrderNumber={orderSummary.purchaseOrderNumber || ''}
        onPurchaseOrderNumberChange={(v) => dispatch(updateSingleField({ field: 'orderSummary.purchaseOrderNumber', value: v }))}
        onDownloadInvoice={() => downloadServicePlanPdf('invoice')}
        onDownloadQuote={() => downloadServicePlanPdf('quote')}
      />
      <hr />
      <PaymentMethodForm
        paymentMethod={paymentMethod.method}
        onPaymentMethodChange={(v) => dispatch(updateSingleField({ field: 'paymentMethod.method', value: v }))}
        hasCardOnFile={hasCardOnFile}
        cardOnFileDisplay={cardOnFileLastFour}
        cardNumber={paymentMethod.cardNumber}
        onCardNumberChange={(v) => dispatch(updateSingleField({ field: 'paymentMethod.cardNumber', value: v }))}
        expirationMonth={paymentMethod.expirationMonth}
        onExpirationMonthChange={(v) => dispatch(updateSingleField({ field: 'paymentMethod.expirationMonth', value: v }))}
        expirationYear={paymentMethod.expirationYear}
        onExpirationYearChange={(v) => dispatch(updateSingleField({ field: 'paymentMethod.expirationYear', value: v }))}
        cvv={paymentMethod.cvv}
        onCvvChange={(v) => dispatch(updateSingleField({ field: 'paymentMethod.cvv', value: v }))}
        errorList={errorList}
        hideCheckOrBankTransfer={isPayBalance}
      />
      <ServiceAddressModal
        show={showAddressModal}
        handleClose={() => setShowAddressModal(false)}
        countryList={countryList}
        serviceAddress={(contactInfo && contactInfo.serviceAddress) || null}
        servicePlanId={(contactInfo && contactInfo.serviceAddress && contactInfo.serviceAddress.id) || ''}
      />
    </div>
  );
}

ServicePlanSummaryForm.propTypes = {
  onChangePlan: PropTypes.func.isRequired,
  onTaxLoadingChange: PropTypes.func.isRequired,
  countryList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    crmId: PropTypes.number,
    code: PropTypes.number,
    name: PropTypes.string,
    abbreviation: PropTypes.string,
  })),
};

ServicePlanSummaryForm.defaultProps = {
  countryList: [],
};

export default ServicePlanSummaryForm;
